import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { LANGUAGES } from 'constants';

/**
 * ResponsiveImage Component
 * Uses <picture> element with media queries for explicit breakpoint control
 * 
 * Accepts a single `images` prop with structure:
 * {
 *   desktop: string | { ar: string, en: string },
 *   tablet: string | { ar: string, en: string },
 *   mobile: string | { ar: string, en: string }
 * }
 * 
 * Breakpoints:
 * - Mobile: max-width 576px
 * - Tablet: max-width 992px
 * - Desktop: above 992px
 */
const ResponsiveImage = ({ 
  images,
  alt, 
  className,
  ...rest 
}) => {
  const { i18n } = useTranslation();
  
  if (!images) return null;

  const langCode = i18n.language?.startsWith(LANGUAGES.ARABIC.langCode) 
    ? LANGUAGES.ARABIC.langCode 
    : LANGUAGES.ENGLISH.langCode;

  const getUrl = (imageValue) => {
    if (!imageValue) return null;
    if (typeof imageValue === 'string') return imageValue;
    return imageValue[langCode] || imageValue.en || null;
  };

  const desktopUrl = getUrl(images.desktop);
  const tabletUrl = getUrl(images.tablet);
  const mobileUrl = getUrl(images.mobile);

  if (!desktopUrl) return null;

  return (
    <picture>
      {/* Mobile: screens up to 576px */}
      {mobileUrl && (
        <source media="(max-width: 576px)" srcSet={mobileUrl} />
      )}

      {/* Tablet: screens from 577px to 992px */}
      {tabletUrl && (
        <source media="(min-width: 577px) and (max-width: 992px)" srcSet={tabletUrl} />
      )}
      
      {/* Desktop: fallback for screens above 992px */}
      <img
        src={desktopUrl}
        alt={alt}
        className={className}
        {...rest}
      />
    </picture>
  );
};

ResponsiveImage.propTypes = {
  images: PropTypes.shape({
    desktop: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ ar: PropTypes.string, en: PropTypes.string })
    ]),
    tablet: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ ar: PropTypes.string, en: PropTypes.string })
    ]),
    mobile: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ ar: PropTypes.string, en: PropTypes.string })
    ])
  }).isRequired,
  alt: PropTypes.string,
  className: PropTypes.string,
};

ResponsiveImage.defaultProps = {
  alt: '',
  className: '',
};

export default ResponsiveImage;
